import React, { useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { formatNumber } from '../../utils/gazTextFormat.js';
import { getGazDeliveryDistances } from './gazTotalBreakdown.js';

/**
 * Километраж плеча при наведении на доставку — МСК Газобетон.
 *
 * Менеджер видел в колонке только сумму и не мог сказать, почему она такая:
 * далеко везти или близко. Расстояние сервер считает и отдаёт давно (в нерудке
 * оно показано отдельной колонкой), в газобетонной таблице его просто не выводили.
 *
 * Своих чисел компонент не считает — всё берётся из той же строки сравнения, что
 * и расшифровка суммы.
 *
 * Механика открытия повторяет GazTotalTooltip: мышью и с клавиатуры — наведение
 * и фокус, на телефоне — нажатие. Там же объяснено, почему состоянием управляем
 * сами, а не встроенным click-триггером.
 */

const GazDeliveryTooltip = ({ entry, children }) => {
  const [show, setShow] = useState(false);
  const pointerTypeRef = useRef('mouse');
  const isTouch = () => pointerTypeRef.current === 'touch';
  const wasOpenRef = useRef(false);

  const rememberPointer = (event) => {
    pointerTypeRef.current = event.pointerType || 'mouse';
  };

  const handlePointerDown = (event) => {
    rememberPointer(event);
    wasOpenRef.current = show;
  };

  const handleToggle = (next) => {
    if (next && isTouch()) {
      return;
    }
    setShow(next);
  };

  const handleClick = (event) => {
    // Выбор производства не переключаем: строка вокруг — тоже орган выбора.
    event.stopPropagation();
    if (!isTouch()) {
      return;
    }
    setShow(!wasOpenRef.current);
  };

  const distances = getGazDeliveryDistances(entry);

  // Расстояния нет — показывать нечего: самовывоз, ошибка расчёта или старый
  // ответ без этих полей. Тогда ячейка остаётся обычным текстом.
  if (!distances) {
    return children;
  }

  // Окно намеренно без заголовка и без рейсов: две строки с километрами и всё
  // (Роман, 20.10.2026). Транспорт, рейсы и цена рейса уже есть в расшифровке
  // суммы — дублировать их здесь значит растить окно ради того же самого.
  const popover = (
    <Popover id="gaz-delivery-popover" className="gaz-cmp__popover" style={{ maxWidth: 'min(92vw, 260px)' }}>
      <Popover.Body className="gaz-cmp__popover-compact">
        {distances.prodToClient !== null && (
          <div>
            От производства: <b>{formatNumber(Math.round(distances.prodToClient))} км</b>
          </div>
        )}
        {distances.kadToClient !== null && (
          <div>
            От КАД: <b>{formatNumber(Math.round(distances.kadToClient))} км</b>
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      show={show}
      onToggle={handleToggle}
      placement="auto"
      flip
      rootClose
      overlay={popover}
      delay={{ show: 120, hide: 100 }}
      popperConfig={{
        modifiers: [
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
          { name: 'offset', options: { offset: [0, 10] } },
        ],
      }}
    >
      <span
        className="gaz-cmp__delivery-value"
        tabIndex={0}
        role="button"
        aria-label="Показать километраж доставки"
        onPointerEnter={rememberPointer}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.stopPropagation();
          }
        }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default GazDeliveryTooltip;
